import { Heading } from "@/components/Heading";
import { Input } from "@/components/Input";
import { Modal } from "@/components/Modal";
import React, { FC, useState } from "react";
import { useTheme } from "@/hooks/useTheme";
import { useFela } from "react-fela";
import { Button } from "@/components/Button";
import axios from "axios";
import { extractFigmaTeamFromURL } from "@/services/figmaService";

type ICountTeamFilesModal = {
  onClose: () => void;
  accessToken?: string;
};

export const CountTeamFilesModal: FC<ICountTeamFilesModal> = ({
  onClose,
  accessToken,
}) => {
  const { colors } = useTheme();
  const [teamId, setTeamId] = useState("");
  const { css, renderer } = useFela();
  const [isLoading, setIsLoading] = useState(false);
  const [teamFilesAmount, setTeamFilesAmount] = useState<number | null>(null);
  const [teamProjectsAmount, setTeamProjectsAmount] = useState<number | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const keyframe = () => ({
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  });

  const animation = renderer.renderKeyframe(keyframe, {});

  const onCount = async () => {
    setIsLoading(true);
    const id = extractFigmaTeamFromURL(teamId) || teamId;

    try {
      const {
        data: { projects, files },
      } = await axios.get("/api/team-files", {
        params: {
          teamId: id,
          accessToken,
        },
      });

      if (!!files && !!projects) {
        setTeamFilesAmount(files);
        setTeamProjectsAmount(projects);
        setError(null);
      }
    } catch {
      setError("Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      id="backup-count"
      customStyle={{
        width: 440,
      }}
      onClose={onClose}
    >
      {isLoading ? (
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: 24,
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          })}
        >
          <div
            className={css({
              border: "4px solid rgba(0, 0, 0, 0.1)",
              borderTopColor: colors.ORANGE,
              borderRadius: "50%",
              width: 40,
              height: 40,
              animation: `${animation} 1.4s infinite cubic-bezier(0.5, 0, 0.5, 1)`,
            })}
          />
          Loading data...
        </div>
      ) : teamFilesAmount === null ? (
        <>
          <Heading
            as="h4"
            customStyle={{
              fontWeight: 600,
              fontSize: 16,
              lineHeight: "24px",
              margin: "0 0 16px",
            }}
          >
            Enter your team ID
          </Heading>
          <Input
            required
            type="email"
            value={teamId}
            onChange={e => setTeamId(e.target.value)}
            placeholder="Insert Figma Team ID"
            customStyle={{
              border: `1px solid ${colors.STARDUST}`,
              padding: "8px 16px",
              borderRadius: 6,
              fontSize: 16,
              width: "100%",
              color: colors.GRAY,
            }}
          />
          <p
            className={css({
              marginTop: 16,
              fontSize: 14,
              color: colors.GRAY,
            })}
          >
            Related Articles:{" "}
            <a
              href="https://help.magicul.io/en/articles/6044499-how-to-backup-all-of-your-team-s-figma-files-with-a-team-id"
              className={css({
                color: colors.BLUE,
                textDecoration: "underline",
              })}
            >
              How do I find my Figma Team ID
            </a>
          </p>
          {!!error && (
            <p
              className={css({
                color: colors.RED,
                fontSize: 14,
                marginTop: 6,
                marginBottom: 0,
              })}
            >
              {error}
            </p>
          )}
          <div
            className={css({
              height: 2,
              width: "100%",
              background: colors.LIGHT_GRAY2,
              margin: "24px 0",
            })}
          />
          <div
            className={css({
              display: "flex",
              gap: 16,
            })}
          >
            <Button title="Count files" onClick={onCount}>
              Count files
            </Button>
            <Button title="Close" variant="DISABLED" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      ) : (
        <>
          <p
            className={css({
              fontWeight: 700,
              fontSize: 18,
              lineHeight: "26px",
              textAlign: "center",
              color: colors.DARK_GREY1,
            })}
          >
            Your team has {teamProjectsAmount} project
            {teamFilesAmount > 1 && "s"} and
          </p>
          <div
            className={css({
              padding: 16,
              background: colors.LIGHT_GRAY2,
              borderRadius: 8,
              fontSize: 28,
              fontWeight: 700,
              marginTop: 24,
              marginBottom: 24,
              textAlign: "center",
              color: colors.DARK_GREY1,
            })}
          >
            {teamFilesAmount} files
          </div>
          <div
            className={css({
              display: "flex",
              gap: 16,
            })}
          >
            <Button
              title="Try another team"
              onClick={() => {
                setTeamId("");
                setTeamFilesAmount(null);
              }}
            >
              Try another team
            </Button>
            <Button title="Close" variant="DISABLED" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};
